import extractQueryParamFromPageLocation from '@src/quince-site-common/src/utils/extractQueryParamFromPageLocation'
import getCookie from '@src/quince-site-common/src/utils/getCookie'
import { getCookieStorage } from '@src/utils/enhancedCookie'

/**
 * OpenAI Ads tracking constants
 * Based on OpenAI Measurement Pixel (oaiq) documentation
 *
 * When a user clicks a ChatGPT ad, OpenAI appends `oppref` to the landing page URL.
 * The oaiq pixel SDK reads this value and persists it in a first-party cookie `__oppref`.
 * oppref is required for conversion events to be attributed to an originating campaign.
 */
export const OPENAI_TRACKING_CONSTANTS = {
  // OpenAI appends oppref to the destination URL on ad click
  CLICK_ID_URL_PARAM: 'oppref',
  // First-party cookie set by OpenAI's oaiq Measurement Pixel
  CLICK_ID_COOKIE: '__oppref',
  // Backup cookie we set when the URL param is present but the oaiq pixel hasn't run yet
  BACKUP_COOKIE: '_quince_oppref',
  // OpenAI recommends 30-day attribution window
  COOKIE_EXPIRY_MINUTES: 30 * 24 * 60,
} as const

const setBackupCookie = (oppref: string): void => {
  if (oppref && typeof window !== 'undefined') {
    const cookieStorage = getCookieStorage()
    cookieStorage.setCookieLocalized(OPENAI_TRACKING_CONSTANTS.BACKUP_COOKIE, oppref, {
      isGlobal: true,
      expires: new Date(Date.now() + OPENAI_TRACKING_CONSTANTS.COOKIE_EXPIRY_MINUTES * 60 * 1000),
      secure: true,
    })
  }
}

/**
 * Extracts the OpenAI click ID (oppref) with proper priority.
 * Priority: URL parameter > oaiq pixel cookie (__oppref) > backup cookie
 *
 * The oaiq Measurement Pixel normally sets __oppref automatically on page load,
 * but we also read directly from the URL and persist a backup so attribution
 * is preserved on subsequent pages even if the pixel fires after our event.
 */
export const getOpenAIClickId = (): string => {
  // 1. URL parameter — authoritative source on the landing page
  const urlClickId: string = extractQueryParamFromPageLocation(OPENAI_TRACKING_CONSTANTS.CLICK_ID_URL_PARAM)
  if (urlClickId) {
    setBackupCookie(urlClickId)
    return urlClickId
  }

  // 2. Cookie set by OpenAI's oaiq pixel SDK
  const pixelCookie = getCookie(OPENAI_TRACKING_CONSTANTS.CLICK_ID_COOKIE)
  if (pixelCookie) {
    return pixelCookie
  }

  // 3. Our backup cookie (handles pages after the initial landing)
  const backupCookie = getCookie(OPENAI_TRACKING_CONSTANTS.BACKUP_COOKIE)
  if (backupCookie) {
    return backupCookie
  }

  return ''
}

export const getOpenAITrackingData = (): { oppref: string } => {
  try {
    return { oppref: getOpenAIClickId() }
  } catch {
    return { oppref: '' }
  }
}
